import {
    Button,
    Card,
    CardBody,
    useDisclosure,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
} from '@nextui-org/react';
import { PlusIcon, StarIcon as StartIconOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import RootAdmin from '../../../../components/layouts/RootAdmin';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { TEKS_TABEL_ID } from "../../../../services/tabelIndonesia";
import useSWR from 'swr';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getFetcher, postCategory, toggleFavoriteCategory } from '../../../../services/api';

export default function IndexPage() {
    const [newCategory, setNewCategory] = useState('');

    const {
        data: newsCategory,
        isLoading: loadingNewsCategory,
        mutate: mutateNews,
    } = useSWR(`/api/content/private/category?type=NEWS`, getFetcher);

    const {
        data: articleCategory,
        isLoading: loadingArticleCategory,
        mutate: mutateArticle,
    } = useSWR(`/api/content/private/category?type=ARTICLE`, getFetcher);

    const {
        data: galleryCategory,
        isLoading: loadingGalleryCategory,
        mutate: mutateGallery,
    } = useSWR(`/api/content/private/category?type=GALLERY`, getFetcher);

    const mergedData = useMemo(() => {
        const newsData = newsCategory?.data ?? [];
        const articleData = articleCategory?.data ?? [];
        const galleryData = galleryCategory?.data ?? [];

        if (newsData.length === 0 && articleData.length === 0 && galleryData.length === 0) {
            return [];
        }

        return [...newsData, ...articleData, ...galleryData].reduce((acc, current) => {
            if (!acc.find((item) => item.name === current.name)) {
                acc.push(current);
            }
            return acc;
        }, []);
    }, [newsCategory, articleCategory, galleryCategory]);

    const filteredData = useMemo(() => {
        if (mergedData.length === 0) return [];
        return mergedData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [mergedData]);

    const refetch = () => {
        mutateNews();
        mutateArticle();
        mutateGallery();
    };

    const {
        isOpen: isOpenModalForm,
        onOpen: onOpenModalForm,
        onOpenChange: onOpenChangeModalForm,
        onClose: onCloseModalForm,
    } = useDisclosure();

    const onToggle = async (rows, isFavorited) => {
        const categoryIds = mergedData.filter((item) => item.name === rows.name).map((item) => item.id);

        const data = {
            isFavorited,
        };

        try {
            await Promise.all(categoryIds.map((id) => toggleFavoriteCategory(id, data)));

            refetch();

            if (data.isFavorited) {
                toast.success('Berhasil Menambahkan Kategori Favorit');
            } else {
                toast.success('Berhasil Menghapus Kategori Favorit');
            }
        } catch (error) {
            console.error('error fetching:', error);
            toast.error('Gagal Update Kategori Favorit');
        }
    };

    const onCreate = async (e) => {
        e.preventDefault();

        try {
            const newsPayload = {
                name: newCategory,
                type: 'NEWS',
                status: 'PUBLISHED',
                author: 'admin',
            };
            await postCategory(newsPayload);

            const articlePayload = {
                name: newCategory,
                type: 'ARTICLE',
                status: 'PUBLISHED',
                author: 'admin',
            };
            await postCategory(articlePayload);

            const galleryPayload = {
                name: newCategory,
                type: 'GALLERY',
                status: 'PUBLISHED',
                author: 'admin',
            };
            await postCategory(galleryPayload);

            refetch();

            toast.success('Berhasil Menambahkan Kategori Baru');
        } catch (error) {
            console.error('error fetching:', error);
            toast.error('Gagal Menambahkan Kategori Baru');
        } finally {
            onCloseModalForm();
        }
    };

    const columns = useMemo(
        () => [
            {
                field: 'id',
                headerName: 'ID',
                width: 90,
            },
            {
                field: 'name',
                headerName: 'Nama Kategori',
                width: 300,
            },
            {
                field: 'isFavorited',
                headerName: 'Favorit',
                width: 150,
                renderCell: (params) => {
                    if (!mergedData.length) return null;

                    let isFavorited = params.row.isFavorited;
                    if (isFavorited) {
                        return (
                            <Button size="sm" onPress={() => onToggle(params.row, false)} color="primary" isIconOnly>
                                <StarIconSolid className="size-5 text-yellow-300" />
                            </Button>
                        );
                    } else {
                        return (
                            <Button size="sm" onPress={() => onToggle(params.row, true)} color="primary" isIconOnly>
                                <StartIconOutline className="size-5" />
                            </Button>
                        );
                    }
                },
            },
        ],
        [mergedData, filteredData]
    );

    return (
        <RootAdmin>
            <Card className="w-full mt-3" radius="sm">
                <CardBody className="w-full h-[550px] p-5">
                    <div className="mb-5">
                        <Button
                            onPress={onOpenModalForm}
                            size="sm"
                            color="primary"
                            startContent={<PlusIcon className="size-4 stroke-2" />}
                        >
                            Tambah
                        </Button>
                    </div>

                    <DataGrid
                        localeText={TEKS_TABEL_ID}
                        rows={filteredData ?? []}
                        loading={loadingNewsCategory || loadingArticleCategory || loadingGalleryCategory}
                        columns={columns}
                        disableDensitySelector
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10,
                                },
                            },
                            density: 'compact',
                        }}
                        slots={{
                            toolbar: GridToolbar,
                        }}
                        pageSizeOptions={[5]}
                        checkboxSelection
                        disableRowSelectionOnClick
                    />
                </CardBody>
            </Card>

            <Modal
                size="xl"
                isOpen={isOpenModalForm}
                onOpenChange={onOpenChangeModalForm}
                onClose={onCloseModalForm}
                isDismissable={false}
                isKeyboardDismissDisabled={false}
                classNames={{
                    wrapper: isOpenModalForm ? 'overflow-hidden' : 'overflow-auto',
                }}
            >
                <ModalContent className="max-h-[90vh] overflow-y-auto">
                    {(onClose) => (
                        <>
                            <ModalHeader>Tambah Kategori</ModalHeader>
                            <ModalBody>
                                <form onSubmit={onCreate}>
                                    <div className="mb-6">
                                        <Input
                                            isRequired
                                            variant="faded"
                                            type="text"
                                            label="Nama Kategori"
                                            onChange={(e) => {
                                                setNewCategory(e.target.value);
                                            }}
                                        />
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <Button isDisabled={!newCategory} type="submit" color="primary">
                                            Tambah
                                        </Button>
                                        <Button color="danger" variant="faded" onPress={onClose}>
                                            Batal
                                        </Button>
                                    </div>
                                </form>
                            </ModalBody>
                            <ModalFooter></ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </RootAdmin>
    );
}
